/**
 * ClaimBanner widget — shown only to guest users.
 * Prompts the user to upgrade their guest account by adding email + password.
 * Tapping the CTA navigates to /aweni/#/claim.
 */

import type { Widget, WidgetContext } from '../types';

export const ClaimBanner: Widget = {
  id: 'claim-banner',
  label: { en: 'Save your account', fr: 'Enregistrer le compte', ar: 'احفظ حسابك', es: 'Guarda tu cuenta', de: 'Konto speichern' },
  icon: '✉',
  defaultVisible: true,
  defaultOrder: -1, // before greeting
  defaultSize: 'expanded',

  visibilityRule: () => {
    try {
      const tok = localStorage.getItem('learnature_token');
      if (!tok) return false;
      const parts = tok.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.is_guest === 1 || payload.is_guest === true;
    } catch {
      return false;
    }
  },

  render(container: HTMLElement, _size: 'compact' | 'expanded', ctx: WidgetContext): void {
    if (!ctx.isGuest) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <div class="claim-banner" role="region" aria-label="${ctx.t('auth_claim_title')}">
        <div class="claim-banner-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        </div>
        <div class="claim-banner-text">
          <div class="claim-banner-title">${ctx.t('auth_guest_mode_label')}</div>
          <div class="claim-banner-msg">${ctx.t('auth_claim_banner')}</div>
        </div>
        <button class="claim-banner-cta" type="button">${ctx.t('auth_claim_banner_cta')}</button>
      </div>
      <style>
        .claim-banner {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 14px; border-radius: 14px;
          background: linear-gradient(135deg, rgba(245,158,11,0.18), rgba(217,119,6,0.10));
          border: 1px solid rgba(245,158,11,0.30);
          color: var(--text-primary);
        }
        .claim-banner-icon {
          flex-shrink: 0;
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(245,158,11,0.25); color: #F59E0B;
          display: flex; align-items: center; justify-content: center;
        }
        .claim-banner-text { flex: 1; min-width: 0; }
        .claim-banner-title { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #F59E0B; }
        .claim-banner-msg { font-size: 0.85rem; color: var(--text-primary); margin-top: 2px; }
        .claim-banner-cta {
          flex-shrink: 0;
          padding: 8px 14px; border-radius: 999px;
          /* #fff on #F59E0B amber = 2.14 contrast (axe-core flagged this).
             #1F1404 (very dark amber) on the same amber yields 5.86 — passes
             WCAG AA for normal text and AAA for large text. The brand still
             reads as the "amber CTA" while being legible. */
          background: #F59E0B; color: #1F1404; font-weight: 700; font-size: 0.82rem;
          border: none; cursor: pointer; min-height: 36px;
        }
        .claim-banner-cta:hover { filter: brightness(1.07); }
      </style>
    `;

    const cta = container.querySelector<HTMLButtonElement>('.claim-banner-cta');
    cta?.addEventListener('click', () => {
      window.location.hash = '#/claim';
    });
  },
};
